// apps/admin/src/features/dashai/lib/onePSfaClient.ts
//
// The auth-retry POST every DashAI Firebase-function client goes through, so
// no caller reimplements the 401-refresh dance. Around 30 workflow clients
// import `postWithAuthRetry` from here; the filename predates that spread and
// no longer describes the contents.

import { auth } from '@/utils/firebase';
import { firebaseFunctionHeaders } from './firebaseFunctionHeaders';

/**
 * POST `body` to `url` with the Firebase ID token as a bearer header. On a
 * 401, force-refreshes the ID token once and retries. The cached token from
 * `auth.currentUser.getIdToken()` can be stale on long-lived tabs;
 * `.getIdToken(true)` forces a fresh mint. We only retry once to avoid loops
 * on genuinely-invalid creds.
 */
export async function postWithAuthRetry(
  url: string,
  body: unknown,
  signal?: AbortSignal,
): Promise<Response> {
  const buildHeaders = async (force: boolean): Promise<Record<string, string>> => {
    const idToken = await auth.currentUser?.getIdToken(force);
    if (!idToken) throw new Error('Not signed in');
    return firebaseFunctionHeaders(idToken);
  };

  let res = await fetch(url, {
    method: 'POST',
    headers: await buildHeaders(false),
    body: JSON.stringify(body),
    signal,
  });
  if (res.status === 401) {
    res = await fetch(url, {
      method: 'POST',
      headers: await buildHeaders(true),
      body: JSON.stringify(body),
      signal,
    });
  }
  return res;
}
